import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { registerHealer, getPublicCentreInfo } from '../utils/api';
import { FiUser, FiPhone, FiMail, FiLock, FiMapPin, FiTag, FiAward } from 'react-icons/fi';
import { INDIAN_STATES } from '../constants/indianStates';

const CERTIFICATIONS = [
  'Basic Pranic Healing',
  'Advanced Pranic Healing',
  'Pranic Psychotherapy',
  'Crystal Healing',
];

const Field = ({ icon, label, name, type = 'text', placeholder, value, onChange, required }) => (
  <div style={S.field}>
    <label style={S.label}>{label}</label>
    <div style={S.inputWrap}>
      <span style={S.inputIcon}>{icon}</span>
      <input
        style={S.input} name={name} type={type}
        placeholder={placeholder} value={value} onChange={onChange}
        required={required}
        autoComplete={type === 'password' ? 'new-password' : undefined}
      />
    </div>
  </div>
);

const RegisterHealer = () => {
  const navigate = useNavigate();

  const params     = new URLSearchParams(window.location.search);
  const centreCode = params.get('centre') || '';
  const ref        = params.get('ref')    || '';
  const invitedBy  = params.get('invitedBy') || '';

  const [centreName,  setCentreName]  = useState('');
  const [form,        setForm]        = useState({ firstName: '', lastName: '', phone: '', email: '', password: '', city: '', state: '', yearsExperience: '' });
  const [certs,       setCerts]       = useState([]);
  const [error,       setError]       = useState('');
  const [submitting,  setSubmitting]  = useState(false);

  useEffect(() => {
    if (!centreCode) return;
    (async () => {
      try {
        const res = await getPublicCentreInfo(centreCode);
        setCentreName(res.data.data?.centre?.tenantName || centreCode);
      } catch {
        setCentreName(centreCode);
      }
    })();
  }, [centreCode]);

  const set  = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const toggleCert = (name) =>
    setCerts(prev => prev.includes(name) ? prev.filter(c => c !== name) : [...prev, name]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!centreCode)    { setError('Centre code is missing. Please use the invite link from your centre.'); return; }
    if (!form.state)    { setError('Please select your state.'); return; }
    setError('');
    setSubmitting(true);
    try {
      await registerHealer({
        firstName:          form.firstName,
        lastName:           form.lastName,
        phone:              form.phone,
        email:              form.email,
        password:           form.password,
        city:               form.city,
        state:              form.state,
        yearsExperience:    form.yearsExperience ? Number(form.yearsExperience) : undefined,
        certifications:     certs.map(name => ({ name })),
        centreCode,
        registrationSource: ref === 'invite' ? 'invite' : 'open',
        invitedBy:          invitedBy || undefined,
      });
      navigate('/register/success');
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
      setSubmitting(false);
    }
  };

  const isInvite = ref === 'invite';

  return (
    <div style={S.page}>
      <div style={S.card}>
        <img src="/logo.png" alt="LifeChiAI" style={S.logo} />
        <h1 style={S.heading}>
          {centreName ? `Join as a Healer at ${centreName}` : 'Join as a Healer'}
        </h1>
        <div style={isInvite ? S.badgeInvite : S.badgeOpen}>
          {isInvite
            ? <><FiTag size={12} style={{ marginRight: 6 }} />Invited by your centre</>
            : <><FiAward size={12} style={{ marginRight: 6 }} />Open Registration</>}
        </div>
        {error && <div style={S.errorBox}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div style={S.row}>
            <Field icon={<FiUser size={14} />}    label="First name" name="firstName" value={form.firstName} onChange={set('firstName')} required />
            <Field icon={<FiUser size={14} />}    label="Last name"  name="lastName"  value={form.lastName}  onChange={set('lastName')}  required />
          </div>
          <Field icon={<FiPhone size={14} />}   label="Phone"    name="phone"    type="tel"      placeholder="+91 98765 43210" value={form.phone}    onChange={set('phone')}    required />
          <Field icon={<FiMail  size={14} />}   label="Email"    name="email"    type="email"    value={form.email}            onChange={set('email')}    required />
          <Field icon={<FiLock  size={14} />}   label="Password" name="password" type="password" value={form.password}         onChange={set('password')} required />
          <Field icon={<FiMapPin size={14} />}  label="City"     name="city"     placeholder="e.g. Chennai"  value={form.city}   onChange={set('city')}   required />
          <div style={S.field}>
            <label style={S.label}>State *</label>
            <select name="state" value={form.state} onChange={set('state')} style={{ ...S.input, paddingLeft: '14px' }} required>
              <option value="">Select state</option>
              {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div style={S.field}>
            <label style={S.label}>Years of experience</label>
            <input style={{ ...S.input, paddingLeft: '14px' }} name="yearsExperience" type="number" min="0" max="50"
              placeholder="e.g. 5" value={form.yearsExperience} onChange={set('yearsExperience')} />
          </div>

          <div style={S.field}>
            <label style={S.label}>Certifications</label>
            <div style={S.certsGrid}>
              {CERTIFICATIONS.map(name => (
                <label key={name} style={S.certItem}>
                  <input
                    type="checkbox" checked={certs.includes(name)}
                    onChange={() => toggleCert(name)} style={{ marginRight: 8, accentColor: '#4a8f82' }}
                  />
                  <span style={{ fontSize: '13px', color: '#333' }}>{name}</span>
                </label>
              ))}
            </div>
          </div>

          <button type="submit" style={{ ...S.btn, opacity: submitting ? 0.7 : 1 }} disabled={submitting}>
            {submitting ? 'Submitting…' : 'Apply to Join'}
          </button>
        </form>
        <div style={S.footer}>
          Already have an account? <a href="/login" style={S.link}>Login</a>
        </div>
      </div>
    </div>
  );
};

const S = {
  page:       { minHeight: '100vh', background: '#f5f8f5', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: "'Open Sans', sans-serif" },
  card:       { background: '#fff', borderRadius: '12px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', padding: '40px', width: '100%', maxWidth: '480px' },
  logo:       { height: '36px', display: 'block', margin: '0 auto 24px' },
  heading:    { fontFamily: "'Raleway', sans-serif", fontSize: '21px', fontWeight: '700', color: '#1a1a2e', margin: '0 0 14px', textAlign: 'center' },
  badgeInvite:{ display: 'inline-flex', alignItems: 'center', background: '#eafaf1', color: '#27ae60', fontSize: '12px', fontWeight: '700', padding: '6px 14px', borderRadius: '20px', marginBottom: '20px', border: '1px solid #a9dfbf' },
  badgeOpen:  { display: 'inline-flex', alignItems: 'center', background: '#eaf4fb', color: '#2980b9', fontSize: '12px', fontWeight: '700', padding: '6px 14px', borderRadius: '20px', marginBottom: '20px', border: '1px solid #aed6f1' },
  errorBox:   { background: '#fdedec', color: '#e74c3c', borderRadius: '8px', padding: '12px 16px', fontSize: '13px', marginBottom: '16px', border: '1px solid #fadbd8' },
  row:        { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  field:      { marginBottom: '14px' },
  label:      { display: 'block', fontSize: '10px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#666', marginBottom: '5px', fontFamily: "'Raleway', sans-serif" },
  inputWrap:  { position: 'relative' },
  inputIcon:  { position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)', color: '#aaa', display: 'flex', pointerEvents: 'none' },
  input:      { width: '100%', padding: '10px 12px 10px 34px', border: '1px solid #e0e0e0', borderRadius: '6px', fontSize: '14px', fontFamily: "'Open Sans', sans-serif", boxSizing: 'border-box', color: '#333', outline: 'none' },
  certsGrid:  { display: 'flex', flexDirection: 'column', gap: '8px', background: '#f9faf7', borderRadius: '8px', padding: '12px 14px', border: '1px solid #e8f0e8' },
  certItem:   { display: 'flex', alignItems: 'center', cursor: 'pointer' },
  btn:        { width: '100%', padding: '13px', background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '8px', fontFamily: "'Raleway', sans-serif", fontSize: '14px', fontWeight: '700', cursor: 'pointer', marginTop: '8px' },
  footer:     { textAlign: 'center', marginTop: '20px', fontSize: '13px', color: '#666' },
  link:       { color: '#4a8f82', fontWeight: '700', textDecoration: 'none' },
};

export default RegisterHealer;
